import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { useAlertsStore, useWorkspaceStore } from '@/stores'
import { workspacesApi } from '@/api/endpoints/workspaces'
import { isModuleEnabledIn, normalizeDashboard, permittedModulesOf } from '@/lib/dashboard'

/**
 * The current workspace's dashboard payload, normalized for the widgets. Keyed by workspace, so a
 * switch shows its own loading state instead of the previous workspace's numbers. Any number of
 * components can call this; they share one request.
 */
export function useWorkspaceDashboard() {
  const workspaceStore = useWorkspaceStore()
  return useQuery({
    queryKey: () => ['workspace-dashboard', workspaceStore.getWorkspaceID],
    queryFn: async ({ signal }) => {
      const body = await workspacesApi.dashboard(workspaceStore.getWorkspaceID, undefined, { signal })
      const dashboard = normalizeDashboard(body.data)
      // The alerts feed mirrors the dashboard's score flags.
      useAlertsStore().syncScoreFlags(dashboard.scoreFlags)
      return dashboard
    },
    enabled: () => Boolean(workspaceStore.getWorkspaceID),
  })
}

const EMPTY = Object.freeze({
  stats: {},
  auditorMetrics: [],
  trainings: [],
  assignedDocuments: [],
  scoreFlags: [],
  skippedSetupSteps: [],
  complianceScore: null,
  policyScore: null,
  totalUsers: 0,
  totalVendors: 0,
  totalTrainings: 0,
  policyMetrics: {},
  policies: {},
  allModules: [],
})

/** Dashboard fields as refs, plus the modules the current user may open (`permittedModules`). */
export function useDashboardModules() {
  const query = useWorkspaceDashboard()
  const dashboard = computed(() => query.data.value ?? EMPTY)
  const field = (name) => computed(() => dashboard.value[name])
  return {
    refetch: query.refetch,
    loadingModules: query.isFetching,
    /** True once the dashboard has answered (successfully or not). */
    dashboardLoaded: computed(() => query.isSuccess.value || query.isError.value),
    stats: field('stats'),
    auditorMetrics: field('auditorMetrics'),
    trainings: field('trainings'),
    assignedDocuments: field('assignedDocuments'),
    scoreFlags: field('scoreFlags'),
    skippedSetupSteps: field('skippedSetupSteps'),
    complianceScore: field('complianceScore'),
    policyScore: field('policyScore'),
    totalUsers: field('totalUsers'),
    totalVendors: field('totalVendors'),
    totalTrainings: field('totalTrainings'),
    policyMetrics: field('policyMetrics'),
    policies: field('policies'),
    allModules: field('allModules'),
    permittedModules: computed(() => permittedModulesOf(dashboard.value.allModules)),
    /** `(name) => boolean`: module switched on for this workspace (false until loaded). */
    isModuleEnabled: (moduleName) => isModuleEnabledIn(dashboard.value.allModules, moduleName),
  }
}
